import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface Product {
  id: string;
  campaignId: string;
  name: string;
  sku: string | null;
  photoS3Key: string | null;
  photoUrl: string | null;
  price: string;
  buyUrl: string;
  deliveryDaysMin: number | null;
  deliveryDaysMax: number | null;
  compatibleModelIds: string[] | null;
  active: boolean;
  outOfStock: boolean;
  clicks: number;
  createdAt: string;
  updatedAt: string;
}

interface Storefront {
  id: string;
  campaignId: string;
  storeName: string;
  searchUrlTemplate: string;
  logoS3Key: string | null;
  logoUrl: string | null;
  avgRating: string | null;
  deliveryLabel: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PreviewData {
  displayMode: 'catalog' | 'storefront' | null;
  partnerName: string;
  products: Product[];
  storefront: Storefront | null;
}

export function useCampaignProducts(campaignId: string) {
  return useQuery<Product[]>({
    queryKey: ['campaign-products', campaignId],
    queryFn: async () => {
      const res = await api.get(`/partners/my/campaigns/${campaignId}/products`);
      return (res.data as { products: Product[] }).products;
    },
    enabled: !!campaignId,
  });
}

export function useCreateProduct(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      sku?: string;
      photoS3Key?: string;
      price: number;
      buyUrl: string;
      deliveryDaysMin?: number;
      deliveryDaysMax?: number;
      compatibleModelIds?: string[];
      active?: boolean;
    }) => {
      const res = await api.post(`/partners/my/campaigns/${campaignId}/products`, data);
      return (res.data as { product: Product }).product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-products', campaignId] });
    },
  });
}

export function useUpdateProduct(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, ...data }: {
      productId: string;
      name?: string;
      sku?: string | null;
      photoS3Key?: string | null;
      price?: number;
      buyUrl?: string;
      deliveryDaysMin?: number | null;
      deliveryDaysMax?: number | null;
      compatibleModelIds?: string[] | null;
      active?: boolean;
      outOfStock?: boolean;
    }) => {
      const res = await api.patch(`/partners/my/campaigns/${campaignId}/products/${productId}`, data);
      return (res.data as { product: Product }).product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-products', campaignId] });
    },
  });
}

export function useDeleteProduct(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (productId: string) => {
      await api.delete(`/partners/my/campaigns/${campaignId}/products/${productId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-products', campaignId] });
    },
  });
}

export function useImportCsv(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const csvContent = await file.text();
      const res = await api.post(
        `/partners/my/campaigns/${campaignId}/products/import-csv`,
        { csvContent },
      );
      return res.data as { imported: number; errors: { line: number; message: string }[] };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-products', campaignId] });
    },
  });
}

export function useCampaignStorefront(campaignId: string) {
  return useQuery<Storefront | null>({
    queryKey: ['campaign-storefront', campaignId],
    queryFn: async () => {
      const res = await api.get(`/partners/my/campaigns/${campaignId}/storefront`);
      return (res.data as { storefront: Storefront | null }).storefront;
    },
    enabled: !!campaignId,
  });
}

export function useUpsertStorefront(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      storeName: string;
      searchUrlTemplate: string;
      logoS3Key?: string;
      logoUrl?: string;
      avgRating?: number;
      deliveryLabel?: string;
    }) => {
      const res = await api.put(`/partners/my/campaigns/${campaignId}/storefront`, data);
      return (res.data as { storefront: Storefront }).storefront;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-storefront', campaignId] });
    },
  });
}

export function usePreview(campaignId: string) {
  return useQuery<PreviewData>({
    queryKey: ['campaign-preview', campaignId],
    queryFn: async () => {
      const res = await api.get(`/partners/my/campaigns/${campaignId}/preview`);
      return res.data as PreviewData;
    },
    enabled: !!campaignId,
  });
}

export function usePresignProductPhoto(campaignId: string) {
  return useMutation({
    mutationFn: async (ext: 'jpg' | 'jpeg' | 'png' | 'webp') => {
      const res = await api.post(`/partners/my/campaigns/${campaignId}/products/presign-photo`, { ext });
      return res.data as { uploadUrl: string; s3Key: string };
    },
  });
}
